import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';

/**
 * Session Progress Bar Component
 * Visual representation of overall session progress with segment indicators
 */
export default function SessionProgressBar({ 
  workflow, 
  currentSegmentIndex, 
  segmentProgress, 
  overallProgress 
}) {
  
  const getToolColor = (toolId) => {
    const colors = {
      'breath-coach': 'bg-cyan-400',
      'pitch-coach': 'bg-purple-400',
      'audition-songs': 'bg-orange-400'
    };
    return colors?.[toolId] || 'bg-gray-400';
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Session Progress</h2>
        <div className="text-white font-bold">{Math.floor(overallProgress)}%</div>
      </div>

      {/* Overall Progress Bar */}
      <div className="h-4 bg-white/10 rounded-full overflow-hidden mb-6">
        <div 
          className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-300"
          style={{ width: `${overallProgress}%` }}
        />
      </div>

      {/* Segment Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {workflow?.map((segment, index) => {
          const isCompleted = index < currentSegmentIndex;
          const isCurrent = index === currentSegmentIndex;
          const color = getToolColor(segment?.tool);

          return (
            <div
              key={segment?.id}
              className={`p-4 rounded-xl transition-all ${
                isCurrent 
                  ? 'bg-white/20 border-2 border-white/40' 
                  : isCompleted
                  ? 'bg-green-500/20 border-2 border-green-500/40' :'bg-white/5 border-2 border-white/10'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <Circle className={`w-5 h-5 ${isCurrent ? 'text-white' : 'text-white/40'}`} />
                )}
                <div className={`w-3 h-3 rounded-sm ${color}`} />
                <span className={`text-sm font-medium ${
                  isCurrent ? 'text-white' : isCompleted ? 'text-green-300' : 'text-white/60'
                }`}>
                  Segment #{index + 1}
                </span>
              </div>
              
              {isCurrent && (
                <div className="mt-2">
                  <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${color} transition-all duration-300`}
                      style={{ width: `${segmentProgress * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}